import { useTranslation } from 'react-i18next';
import React, { useMemo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { HelperTooltip, UnionAvatar, Loading, OfficialAccountFollowBtn } from 'components';
import { IconUserCardStream, IconUserCardTVF, IconUserCardRightArrow } from '@/assets/userCard';
import { useHiConversationModal } from '@/views/Chats/components/HiConversationModal';
import { useHiUserCache, useOfficialAccount, useTheme } from '@/hooks';
import {
  abbreviateNumber,
  convertOfficialHiIdToOriginalId,
  addPrefixToOfficialId,
  openNewWindow,
  getOfficialPath,
} from 'utils';
import style from './index.module.css';
import cx from 'clsx';
import { useOfficialLatestStream } from '@/views/Stream/hooks/list';

type OfficialCardProps = {
  id: string;
  hideSayHiButton?: boolean;
  forceCloseCallback?: () => void;
  restParams?: Record<string, string | number | boolean | undefined>;
  followCallback?: (isFollowing: boolean) => void;
};

export const OfficialInfoCard = React.memo(
  ({ id, hideSayHiButton, restParams, followCallback, forceCloseCallback }: OfficialCardProps) => {
    const { t } = useTranslation();

    const originalOfficialId = useMemo(() => convertOfficialHiIdToOriginalId(String(id)), [id]);
    const officialIdWidthPrefix = useMemo(
      () => addPrefixToOfficialId(originalOfficialId),
      [originalOfficialId],
    );
    const { openHiModal } = useHiConversationModal();
    const { officialAccountDetail } = useOfficialAccount(originalOfficialId);
    const { isDark } = useTheme();
    const { userDict } = useHiUserCache();
    const officialInfo =
      (userDict[officialIdWidthPrefix] as OfficialAccount) || officialAccountDetail;
    const { data: latestStream, isLoading } = useOfficialLatestStream(originalOfficialId);
    const officialPath = getOfficialPath(officialAccountDetail);

    const onClickSayHi = useCallback(() => {
      forceCloseCallback && forceCloseCallback();

      openHiModal({
        hiId: officialIdWidthPrefix,
      });
    }, [forceCloseCallback, officialIdWidthPrefix, openHiModal]);

    // const handleClickRank = useCallback(() => {
    //   openNewWindow('/official-ranking');
    // }, []);

    const previewStream = useMemo(() => {
      if (isLoading) {
        return <Loading />;
      }
      const previewItem = latestStream?.article;

      if (previewItem?.summary) {
        return <span className={style.previewText}>{previewItem?.summary}</span>;
      } else {
        return <span className={style.empty}>{t('OfficialCardPopup.13cbul6')}</span>;
      }
    }, [isLoading, latestStream?.article, t]);

    return (
      <div className={style.userCard}>
        {officialAccountDetail?.banner_url ? (
          <div className={style.bannerBox}>
            <img className={style.banner} src={officialAccountDetail?.banner_url} alt="banner" />
            <div className={cx(style.linearBg, isDark && style.isDark)} />
          </div>
        ) : null}
        <div className={style.userCardInner}>
          <div className={style.header}>
            <UnionAvatar
              data={officialAccountDetail}
              size={36}
              officialLogoOption={{
                placement: 'topLeft',
                align: { targetOffset: [25, 0] },
                tooltipClassName: style.verifyIconTooltip,
              }}
            />
            <div className={style.headerRight}>
              <span
                onClick={() => openNewWindow(officialPath, '_blank')}
                className={style.userName}>
                {officialAccountDetail?.name}
              </span>
              <div className={style.intro}>{officialAccountDetail?.intro} </div>
            </div>
          </div>

          <div className={style.netWorthInfo}>
            <div className={style.left}>
              {/* {officialAccountDetail?.rank_at ? (
              <div onClick={handleClickRank} className={style.rankTag}>
                <IconUserCardCup className={style.tagIcon} />
                {officialAccountDetail?.rank_at}
              </div>
            ) : null} */}

              <div className={style.social}>
                {officialInfo?.tvf ? (
                  <HelperTooltip
                    inParent
                    title={t('OfficialCardPopup.owsomm')}
                    placement="topLeft"
                    className={style.socialItem}>
                    <span className={cx(style.socialItemLabel, style.tvf, style.skewX)}>
                      <IconUserCardTVF
                        className={cx(style.icon, style.skewXReverse)}
                        width={10}
                        height={10}
                      />
                      <span className={cx(style.socialItemTitle, style.skewXReverse)}>TVF</span>
                    </span>
                    <span className={style.socialNum}>
                      ${abbreviateNumber(officialInfo?.tvf || 0, 1)?.toLocaleLowerCase()}
                    </span>
                  </HelperTooltip>
                ) : null}
                {officialAccountDetail?.follower_count ? (
                  <NavLink
                    className={style.socialItem}
                    target={'_blank'}
                    to={`${officialPath}/follower`}>
                    <span className={style.socialItemLabel}>
                      <div className={style.socialItemTitle}>{t('OfficialCardPopup.19t454l')}</div>
                    </span>
                    <span className={style.socialNum}>
                      {abbreviateNumber(officialInfo?.follower_count)}
                    </span>
                  </NavLink>
                ) : null}
              </div>
            </div>
            <div className={style.right}>
              <OfficialAccountFollowBtn
                className={style.followButton}
                restParams={restParams}
                officialId={originalOfficialId}
                followCallback={followCallback}
              />

              {hideSayHiButton ? null : (
                <div className={style.sayHiBtn} onClick={onClickSayHi}>
                  <div className={style.inner}>{t('OfficialCardPopup.14u8n86')}</div>
                </div>
              )}
            </div>
          </div>

          <div className={style.stream}>
            <NavLink className={style.streamTitle} target="_blank" to={`${officialPath}/stream`}>
              <div>
                <IconUserCardStream className={style.icon} />
                {t('OfficialCardPopup.154lpcw')}
              </div>
              <IconUserCardRightArrow />
            </NavLink>
            <div className={style.streamContent}>{previewStream}</div>
          </div>
        </div>
      </div>
    );
  },
);
